import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown, Workflow, GripVertical, Sparkles,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import type { CampoSchema } from './BPM'

type TipoNode = 'inicio' | 'tarefa' | 'fim'
type TipoCampo = CampoSchema['tipo']

const TIPOS_CAMPO: { valor: TipoCampo; label: string }[] = [
  { valor: 'texto', label: 'Texto' },
  { valor: 'texto_longo', label: 'Texto longo' },
  { valor: 'numero', label: 'Número' },
  { valor: 'select', label: 'Lista (select)' },
  { valor: 'data', label: 'Data' },
  { valor: 'anexo', label: 'Anexo' },
  { valor: 'pessoa', label: 'Pessoa (cadastro)' },
  { valor: 'produto', label: 'Produto (cadastro)' },
]

const ICONES = ['ShoppingCart', 'Users', 'FileText', 'Briefcase', 'Sparkles', 'Workflow']
const CORES = ['emerald', 'blue', 'purple', 'amber', 'rose', 'slate']

interface NodeForm {
  codigo: string
  nome: string
  tipo: TipoNode
  descricao?: string
  form_schema?: { campos?: CampoSchema[] }
  atribuicao_tipo?: string
  atribuicao_valor?: string
  sla_horas?: number
  proximo_node_codigo?: string
  ordem: number
}

export default function BPMProcessoEditor() {
  const { id } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const novo = id === 'novo' || !id

  const { data: proc, isLoading } = useQuery<any>({
    queryKey: ['bpm-processo-edit', id],
    queryFn: async () => (await api.get(`/bpm/processos/${id}`)).data,
    enabled: !novo,
  })

  const [form, setForm] = useState<any>({
    codigo: '', nome: '', descricao: '', categoria: '', icone: 'Workflow',
    cor: 'slate', ativo: true, sla_dias: null,
    form_abertura: { campos: [] as CampoSchema[] },
    nodes: [
      { codigo: 'inicio', nome: 'Abertura', tipo: 'inicio', ordem: 0, proximo_node_codigo: 'fim' },
      { codigo: 'fim', nome: 'Concluído', tipo: 'fim', ordem: 1 },
    ] as NodeForm[],
  })

  useEffect(() => {
    if (proc && !novo) {
      setForm({
        codigo: proc.codigo, nome: proc.nome, descricao: proc.descricao || '',
        categoria: proc.categoria || '', icone: proc.icone || 'Workflow',
        cor: proc.cor || 'slate', ativo: proc.ativo, sla_dias: proc.sla_dias,
        form_abertura: proc.form_abertura || { campos: [] },
        nodes: (proc.nodes || []).slice().sort((a: any, b: any) => a.ordem - b.ordem),
      })
    }
  }, [proc, novo])

  const salvar = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        sla_dias: form.sla_dias || null,
        nodes: form.nodes.map((n: NodeForm, i: number) => ({ ...n, ordem: i })),
      }
      if (novo) return (await api.post('/bpm/processos', payload)).data
      return (await api.put(`/bpm/processos/${id}`, payload)).data
    },
    onSuccess: (r) => {
      toast.success('Processo salvo')
      qc.invalidateQueries({ queryKey: ['bpm-processos'] })
      qc.invalidateQueries({ queryKey: ['bpm-processo-edit', id] })
      if (novo) nav(`/bpm/processos/${r.id}/editar`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const apagar = useMutation({
    mutationFn: async () => (await api.delete(`/bpm/processos/${id}`)).data,
    onSuccess: () => {
      toast.success('Processo apagado')
      qc.invalidateQueries({ queryKey: ['bpm-processos'] })
      nav('/bpm')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  function moverNode(idx: number, delta: number) {
    const novoIdx = idx + delta
    if (novoIdx < 0 || novoIdx >= form.nodes.length) return
    const novos = form.nodes.slice()
    const [n] = novos.splice(idx, 1)
    novos.splice(novoIdx, 0, n)
    setForm({ ...form, nodes: novos })
  }

  function addNode() {
    const idx = form.nodes.findIndex((n: NodeForm) => n.tipo === 'fim')
    const inserir = idx >= 0 ? idx : form.nodes.length
    const novoNode: NodeForm = {
      codigo: `tarefa_${Date.now().toString(36).slice(-4)}`,
      nome: 'Nova tarefa',
      tipo: 'tarefa',
      ordem: inserir,
      atribuicao_tipo: 'perfil',
      atribuicao_valor: 'admin',
      form_schema: { campos: [] },
    }
    const novos = form.nodes.slice()
    novos.splice(inserir, 0, novoNode)
    setForm({ ...form, nodes: novos })
  }

  function removeNode(idx: number) {
    const n = form.nodes[idx]
    if (n.tipo !== 'tarefa') {
      toast.error('Só nós de tarefa podem ser removidos')
      return
    }
    setForm({ ...form, nodes: form.nodes.filter((_: any, i: number) => i !== idx) })
  }

  function updateNode(idx: number, changes: Partial<NodeForm>) {
    const novos = form.nodes.slice()
    novos[idx] = { ...novos[idx], ...changes }
    setForm({ ...form, nodes: novos })
  }

  // Recalcula proximo_node_codigo baseado na ordem (fluxo linear)
  useEffect(() => {
    const novos = form.nodes.map((n: NodeForm, i: number) => {
      if (n.tipo === 'fim') return { ...n, proximo_node_codigo: null }
      const prox = form.nodes[i + 1]
      return { ...n, proximo_node_codigo: prox?.codigo || null }
    })
    // Evita loop infinito: só atualiza se mudou
    const mudou = novos.some((n: NodeForm, i: number) => n.proximo_node_codigo !== form.nodes[i].proximo_node_codigo)
    if (mudou) setForm((f: any) => ({ ...f, nodes: novos }))
  }, [form.nodes.map((n: NodeForm) => n.codigo).join(',')])

  if (isLoading) return <div className="p-6">Carregando...</div>

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <button className="btn-ghost" onClick={() => nav('/bpm')}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="w-6 h-6" /> {novo ? 'Novo processo' : form.nome}
          </h1>
          <p className="text-sm text-ink-500">Edite os nós do fluxo e os campos de cada etapa</p>
        </div>
        {!novo && (
          <>
            <button onClick={() => nav(`/bpm/processos/${id}/designer`)} className="btn-secondary">
              <Sparkles className="w-4 h-4" /> Designer visual
            </button>
            <button onClick={() => pedirExclusao({ descricao: 'o processo', onConfirm: () => apagar.mutate() })}
              className="btn-ghost text-rose-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="btn-primary">
          <Save className="w-4 h-4" /> {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      <div className="card card-body mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Código *</label>
          <input className="input font-mono" value={form.codigo}
            onChange={e => setForm({ ...form, codigo: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
            placeholder="solicitacao_compra" />
        </div>
        <div>
          <label className="label">Nome *</label>
          <input className="input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className="label">Descrição</label>
          <input className="input" value={form.descricao}
            onChange={e => setForm({ ...form, descricao: e.target.value })} />
        </div>
        <div>
          <label className="label">Categoria</label>
          <input className="input" value={form.categoria}
            onChange={e => setForm({ ...form, categoria: e.target.value })}
            placeholder="compras, rh, contratos..." />
        </div>
        <div>
          <label className="label">SLA (dias)</label>
          <input type="number" className="input" value={form.sla_dias ?? ''}
            onChange={e => setForm({ ...form, sla_dias: e.target.value ? Number(e.target.value) : null })} />
        </div>
        <div>
          <label className="label">Ícone</label>
          <select className="input" value={form.icone} onChange={e => setForm({ ...form, icone: e.target.value })}>
            {ICONES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Cor</label>
          <select className="input" value={form.cor} onChange={e => setForm({ ...form, cor: e.target.value })}>
            {CORES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 mt-5">
          <input type="checkbox" checked={form.ativo}
            onChange={e => setForm({ ...form, ativo: e.target.checked })} />
          Ativo
        </label>
      </div>

      <div className="card mb-4">
        <div className="px-4 py-3 border-b border-ink-200 font-semibold">Formulário de abertura</div>
        <div className="p-4">
          <CamposEditor
            campos={form.form_abertura?.campos || []}
            onChange={(campos) => setForm({ ...form, form_abertura: { campos } })}
          />
        </div>
      </div>

      <div className="card">
        <div className="px-4 py-3 border-b border-ink-200 font-semibold flex items-center justify-between">
          <span>Fluxo — {form.nodes.length} nós</span>
          <button onClick={addNode} className="btn-primary !py-1 !px-3 text-xs">
            <Plus className="w-3 h-3" /> Adicionar tarefa
          </button>
        </div>
        <div className="divide-y divide-ink-100">
          {form.nodes.map((n: NodeForm, idx: number) => (
            <NodeCard key={idx} node={n} idx={idx}
              podeMover={n.tipo === 'tarefa'}
              onChange={(ch: Partial<NodeForm>) => updateNode(idx, ch)}
              onRemove={() => removeNode(idx)}
              onMoveUp={() => moverNode(idx, -1)}
              onMoveDown={() => moverNode(idx, 1)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function NodeCard({ node, idx, podeMover, onChange, onRemove, onMoveUp, onMoveDown }: any) {
  const [open, setOpen] = useState(false)
  const corTipo = {
    inicio: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    tarefa: 'bg-blue-100 text-blue-700 border-blue-200',
    fim: 'bg-ink-100 text-ink-600 border-ink-200',
  }[node.tipo as TipoNode]

  return (
    <div className="p-3">
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-ink-300" />
        <div className={`px-2 py-0.5 text-[10px] rounded border font-medium uppercase ${corTipo}`}>
          {node.tipo}
        </div>
        <div className="flex-1">
          <input className="input !py-1 !text-sm" value={node.nome}
            onChange={e => onChange({ nome: e.target.value })}
            disabled={!podeMover} />
        </div>
        {podeMover && (
          <>
            <button onClick={() => setOpen(!open)} className="btn-ghost !p-1">
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button onClick={onMoveUp} className="btn-ghost !p-1"><ChevronUp className="w-4 h-4" /></button>
            <button onClick={onMoveDown} className="btn-ghost !p-1"><ChevronDown className="w-4 h-4" /></button>
            <button onClick={onRemove} className="btn-ghost !p-1 text-rose-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
      {open && podeMover && (
        <div className="ml-8 mt-3 p-3 bg-ink-50 rounded space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Código</label>
              <input className="input font-mono" value={node.codigo}
                onChange={e => onChange({ codigo: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
            </div>
            <div>
              <label className="label">SLA (horas)</label>
              <input type="number" className="input" value={node.sla_horas ?? ''}
                onChange={e => onChange({ sla_horas: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <div>
              <label className="label">Atribuir a</label>
              <select className="input" value={node.atribuicao_tipo || ''}
                onChange={e => onChange({ atribuicao_tipo: e.target.value })}>
                <option value="">Ninguém (livre)</option>
                <option value="perfil">Por perfil</option>
                <option value="usuario">Usuário específico</option>
                <option value="solicitante">Solicitante</option>
              </select>
            </div>
            <div>
              <label className="label">Valor</label>
              <input className="input" value={node.atribuicao_valor || ''}
                onChange={e => onChange({ atribuicao_valor: e.target.value })}
                placeholder={node.atribuicao_tipo === 'perfil' ? 'admin, operador...' : 'ID do usuário'} />
            </div>
          </div>
          <div>
            <label className="label">Campos do formulário</label>
            <CamposEditor
              campos={node.form_schema?.campos || []}
              onChange={(campos) => onChange({ form_schema: { campos } })}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function CamposEditor({ campos, onChange }: {
  campos: CampoSchema[]; onChange: (c: CampoSchema[]) => void
}) {
  function add() {
    onChange([...campos, { chave: `campo_${campos.length + 1}`, label: 'Novo campo', tipo: 'texto' }])
  }
  function update(i: number, ch: Partial<CampoSchema>) {
    const n = campos.slice(); n[i] = { ...n[i], ...ch }; onChange(n)
  }
  function remove(i: number) {
    onChange(campos.filter((_, idx) => idx !== i))
  }

  return (
    <div className="space-y-2">
      {campos.map((c, i) => (
        <div key={i} className="flex gap-2 items-start bg-white p-2 rounded border border-ink-200">
          <input className="input !py-1 text-xs flex-1" placeholder="Chave"
            value={c.chave}
            onChange={e => update(i, { chave: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
          <input className="input !py-1 text-xs flex-1" placeholder="Label"
            value={c.label} onChange={e => update(i, { label: e.target.value })} />
          <select className="input !py-1 text-xs" value={c.tipo}
            onChange={e => update(i, { tipo: e.target.value as TipoCampo })}>
            {TIPOS_CAMPO.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
          </select>
          {c.tipo === 'select' && (
            <input className="input !py-1 text-xs flex-1" placeholder="opções separadas por vírgula"
              value={c.opcoes?.join(', ') || ''}
              onChange={e => update(i, { opcoes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
          )}
          <label className="flex items-center gap-1 text-xs whitespace-nowrap">
            <input type="checkbox" checked={c.obrigatorio || false}
              onChange={e => update(i, { obrigatorio: e.target.checked })} />
            obrig.
          </label>
          <button onClick={() => remove(i)} className="btn-ghost !p-1 text-rose-600">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
      <button onClick={add} className="btn-secondary !py-1 text-xs w-full">
        <Plus className="w-3 h-3" /> Adicionar campo
      </button>
    </div>
  )
}
